import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, LogOut, ShieldCheck, Newspaper, Calculator, LayoutList } from "lucide-react";

const BRIEFING_ITEMS = [
  { label: "Briefing", path: "/", icon: Newspaper, key: "home" },
  { label: "Freight Rates & Cost Avoidance", path: "/cost-avoidance", icon: Calculator, key: "cost-avoidance" },
  { label: "Monthly Reports", path: "/reports", icon: LayoutList, key: "reports" },
];

const linkCls = (active) =>
  `px-3 py-1.5 rounded-md text-sm whitespace-nowrap uppercase tracking-tight transition-colors ${
    active ? "text-accent font-bold" : "text-slate-600 hover:text-slate-900 font-semibold"
  }`;

export function PrimaryNav({ sections, onSectionClick }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const briefingActive =
    BRIEFING_ITEMS.some((i) => i.path === pathname) || pathname.startsWith("/report");

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${linkCls(briefingActive)} inline-flex items-center gap-1 font-display`} data-testid="nav-briefing-group">
            ASIA–AU Briefing <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 max-h-[72vh] overflow-y-auto">
          {BRIEFING_ITEMS.map((i) => {
            const Icon = i.icon;
            return (
              <DropdownMenuItem key={i.key} onClick={() => navigate(i.path)} data-testid={`nav-briefing-${i.key}`} className="gap-2 cursor-pointer">
                <Icon className="w-4 h-4 text-slate-400" /> {i.label}
              </DropdownMenuItem>
            );
          })}
          {sections?.length ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400 font-num">Jump to section</DropdownMenuLabel>
              {sections.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => onSectionClick?.(s.id)} data-testid={`nav-section-${s.id}`} className="cursor-pointer text-slate-600 pl-6">
                  {s.label}
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <button onClick={() => navigate("/supply-chain")} className={linkCls(pathname === "/supply-chain")} data-testid="nav-supply-chain-link">Supply Chain</button>
      <button onClick={() => navigate("/procurement")} className={linkCls(pathname === "/procurement")} data-testid="nav-procurement-link">Procurement</button>
      <button onClick={() => navigate("/budget-assumptions")} className={linkCls(pathname === "/budget-assumptions")} data-testid="nav-budget-link">Budget 27–28</button>
    </div>
  );
}

export function NavRight() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div className="flex items-center gap-2 shrink-0">
      {user?.role === "admin" && (
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary px-2 py-1.5" data-testid="nav-admin-button">
          <ShieldCheck className="w-4 h-4" /> Admin
        </button>
      )}
      <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-destructive px-2 py-1.5" data-testid="nav-logout-button">
        <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
      </button>
    </div>
  );
}

export function AppNav({ badge }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 h-14">
          <nav className="flex-1 flex items-center overflow-x-auto no-scrollbar">
            <PrimaryNav />
          </nav>
          {badge}
          <NavRight />
        </div>
      </div>
    </header>
  );
}
